import { useState, useEffect, useMemo, useCallback, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import axios from "axios";
import ExportSbomReportModal from "@components/UI/ExportSbomReportModal";
import queryKeys from "@configs/react-query/query-key";
import Breadcrumb from "@components/UI/Breadcrumb";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ComponentTable } from "@components/Tables";
import { ChevronUpIcon, DocumentInfoIcon, PlusCircleIcon, TrashIcon } from "@components/Icons";
import OutlineButton from "@components/Buttons/OutlineButton";
import { PrimaryButton } from "@components/Buttons";
import { CustomInput } from "@components/Forms";
import { formatDateTime, removeSpaces, normalizeComponentsWithSeq, deduplicateComponents, formatVerificationType, getAppLanguage, hasMissingComponentNames } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { formatUserNameWithId } from "@utils/userDisplay";
import { extractErrorCode } from "@utils/error";
import VerificationFileDetailsModal from "@components/Dialogs/VerificationFileDetailsModal";
import ProjectDetailsModal from "@components/Dialogs/ProjectDetailsModal";
import NoVerificationFileErrorModal from "@components/Dialogs/NoVerificationFileErrorModal";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import SelectVerificationFilesForSbomMergeModal from "@components/UI/SelectVerificationFilesForSbomMergeModal";
import useSbomMerge from "@hooks/useSbomMerge";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { LOCAL_STORAGE, CHARACTER_LENGTH } from "@constants/index";
import { VEX_SNAPSHOT_SOURCE, resolveVexScanToolCd } from "@constants/vex";
import { buildSbomDownloadPayload, getSbomExportResponseType, isSbomHtmlReadableExport, resolveSbomExportLanguage } from "@constants/sbom";
import {
  openSbomReadableViewOrToast,
  SBOM_READABLE_VIEW_SOURCE,
} from "@utils/sbomReadableView";
import { getLocalStorage } from "@utils/local-storage";
import { CveMergedModal, ComponentInformationModal, LicenseInformationModal, } from "@components/UI";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import { PermissionContext } from "@contexts/PermissionContext";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";

const TEST_ID = "sbom-merge-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

const SbomMergeDetail = () => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { mergedSbomSeq } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasEditPermission } = useRoutePermissions();
  const { hasProjectManagementPermission } = useContext(PermissionContext);
  const canEditMergedVex = hasEditPermission && hasProjectManagementPermission;
  const [sbomMergedData, setSbomMergedData] = useState(null);
  const [verificationFilesData, setVerificationFilesData] = useState([]);
  const [sbomMergedComponentList, setSbomMergedComponentList] = useState([]);
  const [isComponentListCollapsed, setIsComponentListCollapsed] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [selectedFileForView, setSelectedFileForView] = useState(null);
  const [showFileDetailsModal, setShowFileDetailsModal] = useState(false);
  const [isEditVerificationFiles, setIsEditVerificationFiles] = useState(false);
  const [showSelectFilesModal, setShowSelectFilesModal] = useState(false);
  const [originalVerificationFiles, setOriginalVerificationFiles] = useState([]);
  const [originalSbomMergedComponentList, setOriginalSbomMergedComponentList] = useState([]);
  const [errors, setErrors] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showNoVerificationFileErrorModal, setShowNoVerificationFileErrorModal] = useState(false);
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);
  const [selectedProjectSeq, setSelectedProjectSeq] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cveMergedData, setCveMergedData] = useState([]);
  const [isMergeTooltipOpen, setIsMergeTooltipOpen] = useState(false);
  const [verificationFilesDataPagination, setVerificationFilesDataPagination] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.SBOM_MERGE_VERIFICATION_FILE_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLicenseInformationModalOpen, setIsLicenseInformationModalOpen] = useState(false);
  const [selectedComponentData, setSelectedComponentData] = useState(null);
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false);
  const [selectedLicenseInformationData, setSelectedLicenseInformationData] = useState(null);
  const [addFlag, setAddFlag] = useState(false);
  const [isRebuildingComponents, setIsRebuildingComponents] = useState(false);
  const componentRequestGenerationRef = useRef(0);

  const breadcrumbItems = [
    { label: t("project.sbom-merged.sbom-merge-list"), href: "/sbom-mergeds" },
    { label: t("navbar.sbom-merge") },
  ];

  const getData = useFetchData(
    [queryKeys.getSbomMerged, mergedSbomSeq],
    `${queryKeys.getSbomMerged}/${mergedSbomSeq}`,
    {
      method: "GET",
    },
    {},
    {
      enabled: !!mergedSbomSeq,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const getVerificationFiles = useMutationData(
    queryKeys.getMergedSbomsVerificationFiles,
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setVerificationFilesDataPagination(response.object);
          setTotalPages(Math.ceil((response.objectCount || 0) / itemsPerPage));
        }
      },
      onError: (error) => {
        console.error("Error during get data Verification Files:", error);
      },
    },
  );

  // Get all license policies list
  const getLicensePolicyList = useMutationData(
    queryKeys.getListLicensePolicy,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string" && response) {
          return Array.isArray(response) ? response : (response.object || []);
        }
        return [];
      },
      onError: (error) => {
        console.error("Error fetching license policy list:", error);
        return [];
      },
    },
  );

  // Helper function to map license policies to components
  const mapLicensePoliciesToComponents = async (components) => {
    if (!Array.isArray(components) || components.length === 0) {
      return components;
    }

    try {
      const licensePolicyList = await getLicensePolicyList.mutateAsync();
      let licensePolicyMap = new Map();

      if (Array.isArray(licensePolicyList) && licensePolicyList.length > 0) {
        licensePolicyList.forEach((policy) => {
          if (policy.licensePolicySeq) {
            licensePolicyMap.set(policy.licensePolicySeq, policy);
          }
        });
      }

      // Attach licensePolicy to each component based on licensePolicySeq
      return components.map((component) => ({
        ...component,
        licensePolicy: component.licensePolicySeq
          ? licensePolicyMap.get(component.licensePolicySeq) || null
          : null,
      }));
    } catch (error) {
      console.error("Error mapping license policies to components:", error);
      // Return components without license policies if fetch fails
      return components;
    }
  };

  useEffect(() => {
    const response = getData.data;
    if (!response || getData.isLoading || getData.error || typeof response === "string") {
      return;
    }

    let cancelled = false;

    const applySbomMergedData = async () => {
      let normalizedComponents = normalizeComponentsWithSeq(response.mergedSbomComponents || []);
      normalizedComponents = await mapLicensePoliciesToComponents(normalizedComponents);
      if (cancelled) {
        return;
      }
      const uniqueComponents = deduplicateComponents(normalizedComponents);
      setSbomMergedData(response);
      setVerificationFilesData(response.verificationFiles);
      setSbomMergedComponentList(uniqueComponents);
      setOriginalVerificationFiles(response.verificationFiles || []);
      setOriginalSbomMergedComponentList(uniqueComponents);
    };

    applySbomMergedData();

    return () => {
      cancelled = true;
    };
  }, [getData.data, getData.isLoading, getData.error]);

  useEffect(() => {
    const error = getData.error;
    if (!error) {
      return;
    }
    if (error?.response?.status === 404) {
      navigate("/404", { state: { backTo: "/sbom-mergeds" } });
    } else {
      ToastConstant(TOAST.error, t("toast.sbom.fetch-error.title"), t("toast.sbom.fetch-error.message"));
    }
    console.error("Error during get data:", error);
  }, [getData.error, navigate, t]);

  const updateSbomMerged = useMutationData(
    queryKeys.updateSbomMerged,
    {
      method: "PUT",
    },
    {
      onSuccess: async (response) => {
        await getData.refetch();
        await queryClient.invalidateQueries({ queryKey: [queryKeys.vexSnapshot] });
        setIsEditMode(false);
        setEditFormData({});
        await updateDataInIndexedDb(STORES.MERGED_SBOMS, response, response.mergedSbomSeq);
      },
      onError: (error) => {
        console.error("Error during update SBOM Merged:", error);
      },
    },
  );

  const updateSbomMergedOnly = useMutationData(
    "merged_sbom_only",
    {
      method: "PUT",
    },
    {
      onSuccess: async (response) => {
        // Refresh the data after successful update
        getData.refetch();
        setIsEditMode(false);
        setEditFormData({});
        await updateDataInIndexedDb(STORES.MERGED_SBOMS, response, response.mergedSbomSeq);
      },
      onError: (error) => {
        console.error("Error during update SBOM Merged:", error);
      },
    },
  );

  const deleteSbomMerged = useMutationData(
    queryKeys.deleteSbomMergeds,
    { method: "DELETE" },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("toast.sbom.merged-deleted-success.title"), t("toast.sbom.merged-deleted-success.message"));
        navigate("/sbom-mergeds");
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        ToastConstant(TOAST.error, t("toast.sbom.merged-delete-failed.title"), t("toast.sbom.merged-delete-failed.message"));
      },
    },
  );

  // Use the SBOM merge hook to fetch component data
  const { fetchComponentsForVerificationFiles } = useSbomMerge();

  const rebuildComponentsFromSelectedFiles = useCallback(async (selectedFiles) => {
    const requestGeneration = ++componentRequestGenerationRef.current;
    setIsRebuildingComponents(true);
    try {
      const components = selectedFiles.length
        ? await fetchComponentsForVerificationFiles(selectedFiles)
        : [];
      if (requestGeneration !== componentRequestGenerationRef.current) return;
      // Do not remap mergedSbomComponentSeq — live rebuild rows must lack it so
      // isLiveVexQuery switches to GET /vex (same as AddSbomMerge / previous behavior).
      setSbomMergedComponentList(components);
      setErrors((prev) => {
        if (!prev.componentNames) return prev;
        if (hasMissingComponentNames(components)) return prev;
        return { ...prev, componentNames: undefined };
      });
    } finally {
      if (requestGeneration === componentRequestGenerationRef.current) {
        setIsRebuildingComponents(false);
      }
    }
  }, [fetchComponentsForVerificationFiles]);

  /**
   * After remove/add, component list is live verification rows (no mergedSbomComponentSeq).
   * Frozen /vex/merged-sbom cannot map those rows — use live GET /vex like AddSbomMerge.
   */
  const isLiveVexQuery = useMemo(() => {
    const list = sbomMergedComponentList;
    if (!Array.isArray(list) || list.length === 0) return false;
    return !list.some((row) => row?.mergedSbomComponentSeq != null);
  }, [sbomMergedComponentList]);

  useEffect(() => {
    if (sbomMergedComponentList.length > 0) {
      const allCves = sbomMergedComponentList
        .filter(component => component.cve && component.cve !== "")
        .map(component => component.cve);

      const uniqueCves = [...new Set(allCves)];
      setCveMergedData(uniqueCves);
    }
  }, [sbomMergedComponentList]);

  useEffect(() => {
    if (mergedSbomSeq && !isEditVerificationFiles) {
      const payload = {
        mergedSbomSeq: mergedSbomSeq,
        startRow: (currentPage - 1) * itemsPerPage,
        pageSize: itemsPerPage,
      };
      getVerificationFiles.mutate(payload);
    }
  }, [currentPage, mergedSbomSeq, isEditVerificationFiles, itemsPerPage]);

  useEffect(() => {
    if (isEditVerificationFiles) {
      const newTotalPages = Math.ceil(verificationFilesData.length / itemsPerPage);
      setTotalPages(newTotalPages);

      let adjustedPage = currentPage;
      if (currentPage > newTotalPages && newTotalPages > 0) {
        adjustedPage = newTotalPages;
      } else if (verificationFilesData.length === 0) {
        adjustedPage = 1;
      }
      setCurrentPage(adjustedPage);

      const startIndex = (adjustedPage - 1) * itemsPerPage;
      const paginatedData = verificationFilesData.slice(startIndex, startIndex + itemsPerPage);
      setVerificationFilesDataPagination(paginatedData);
    }
  }, [isEditVerificationFiles, verificationFilesData, itemsPerPage, currentPage]);

  // Handle edit mode functions
  const handleEditClick = () => {
    if (sbomMergedData) {
      setEditFormData({
        mergedName: sbomMergedData.mergedName || "",
        copyright: sbomMergedData.copyright || "",
        remark: sbomMergedData.remark || "",
      });
      setIsEditMode(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData({});
  };

  const handleSaveEdit = () => {
    // Prepare the data for the API call
    const updateData = {
      ...sbomMergedData,
      mergedName: editFormData.mergedName,
      copyright: editFormData.copyright,
      remark: editFormData.remark,
    };

    // Call the API to update the SBOM Merged
    updateSbomMerged.mutate(updateData);
  };

  const handleSaveEditForInfo = () => {
    // Prepare the data for the API call
    const updateData = {
      ...sbomMergedData,
      mergedName: editFormData.mergedName,
      copyright: editFormData.copyright,
      remark: editFormData.remark,
    };

    // Call the API to update the SBOM Merged
    updateSbomMergedOnly.mutate(updateData);
  };

  const handleInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleExportSbomReport = async (selection) => {
    const { type, format, includeVex } = selection || {};
    if (!type) return;

    setIsExporting(true);
    setShowExportModal(false);

    if (isSbomHtmlReadableExport(type)) {
      try {
        openSbomReadableViewOrToast({
          sourceType: SBOM_READABLE_VIEW_SOURCE.MERGED,
          params: {
            mergedSbomSeq,
            includeVex: includeVex ? "Y" : "N",
          },
          title: sbomMergedData?.mergedName || "",
        });
      } finally {
        setIsExporting(false);
      }
      return;
    }

    handleDownloadFormatSelectWithVex(type, includeVex, format);
  };

  const handleDownloadFormatSelectWithVex = async (sbomType, includeVex = false, format = "JSON") => {
    try {
      const token = getLocalStorage(LOCAL_STORAGE.TOKEN);
      let url = `/mergedSbomDownload`;
      const params = {
        mergedSbomSeq: mergedSbomSeq,
        sbomType: sbomType.value,
        format,
        language: resolveSbomExportLanguage(format, getAppLanguage()),
      };
      
      if (includeVex) {
        params.includeVex = "Y";
      } else {
        params.includeVex = "N";
      }

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: getSbomExportResponseType(format),
      });

      if (response.status === 200) {
        const currentTimestamp = dayjs().format("YYYYMMDD");
        const { blob, extension } = buildSbomDownloadPayload(response.data, format);
        
        let fileName;
        if (format === "CSAF") {
          fileName = `${removeSpaces(sbomMergedData.mergedName)}_CSAF_${currentTimestamp}.${extension}`;
        } else {
          fileName = `${removeSpaces(sbomMergedData.mergedName)}_${removeSpaces(sbomType.label)}_${currentTimestamp}.${extension}`;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error exporting JSON:", error);
      const errorCodeKey = extractErrorCode(error, "errorcode.00001");
      const errorMessage = errorCodeKey ? t(errorCodeKey) : (error.response?.data?.message || error.response?.data?.additionalMessage || t("toast.export.error.message"));
      ToastConstant(TOAST.error, t("toast.export.error.title"), errorMessage);
    }

    setIsExporting(false);
  };

  const handleViewFile = (file) => {
    setSelectedFileForView(file);
    setShowFileDetailsModal(true);
  };

  const handleCloseFileDetailsModal = () => {
    setShowFileDetailsModal(false);
    setSelectedFileForView(null);
  };

  const handleEditVerificationFiles = () => {
    // Store the original verification files and component list before editing
    setOriginalVerificationFiles([...verificationFilesData]);
    setOriginalSbomMergedComponentList([...sbomMergedComponentList]);
    setIsEditVerificationFiles(true);
  };

  const handleCancelVerificationFiles = () => {
    componentRequestGenerationRef.current += 1;
    setIsRebuildingComponents(false);
    setErrors([]);
    setVerificationFilesData([...originalVerificationFiles]);
    setSbomMergedComponentList([...originalSbomMergedComponentList]);
    setIsEditVerificationFiles(false);

    // Calculate new total pages based on original verification files
    const newTotalPages = Math.ceil(originalVerificationFiles.length / itemsPerPage);

    // Adjust currentPage to ensure it doesn't exceed
    let adjustedPage = currentPage;
    if (currentPage > newTotalPages && newTotalPages > 0) {
      adjustedPage = newTotalPages; // Set to last valid page
    } else if (originalVerificationFiles.length === 0) {
      adjustedPage = 1; // Default to page 1 if no files
    }
    setCurrentPage(adjustedPage);

    // Refresh paginated data
    const payload = {
      mergedSbomSeq: mergedSbomSeq,
      startRow: (currentPage - 1) * itemsPerPage,
      pageSize: itemsPerPage,
    };
    getVerificationFiles.mutate(payload);
  };

  const handleSaveVerificationFiles = async () => {
    if (addFlag) {
      const newErrors = {};

      // Validate verification files
      if (verificationFilesData.length < 2) {
        newErrors.verificationFiles = "project.sbom-merged.validation-two-project-versions-msg";
      }

      if (hasMissingComponentNames(sbomMergedComponentList)) {
        newErrors.componentNames = "project.sbom-merged.validation-component-name-msg";
      }

      // If there are errors, set them and return
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);

        // Show modal for length = 0 case
        if (verificationFilesData.length === 0) {
          setShowNoVerificationFileErrorModal(true);
        }
        if (newErrors.componentNames) {
          ToastConstant(
            TOAST.error,
            t("toast.sbom.merged-component-name-required.title"),
            t("toast.sbom.merged-component-name-required.message"),
          );
        }
        return;
      }

      // Clear any existing errors
      setErrors({});

      // Create the payload for updating SBOM merged
      const requestData = {
        ...sbomMergedData,
        verificationFiles: verificationFilesData.map(file => ({
          verificationType: file.verificationType,
          projectSeq: file.projectSeq,
          projectId: file.projectId,
          projectName: file.projectName,
          verificationVersion: file.verificationVersion,
          realFileName: file.realFileName,
          verificationSeq: file.verificationSeq
        })),
        mergedSbomComponents: sbomMergedComponentList
      };

      try {
        await updateSbomMerged.mutateAsync(requestData);
        setIsEditVerificationFiles(false);
        setAddFlag(false);
      } catch {
        // Keep the explicit edit state so the user can retry or cancel back to the frozen saved state.
      }
    } else {
      setIsEditVerificationFiles(false);
      setAddFlag(false);
    }
  };

  const handleAddVerificationFile = () => {
    setShowSelectFilesModal(true);
  };

  const handleDeleteMergedSbom = () => {
    setShowNoVerificationFileErrorModal(false);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteSbomMerged.mutateAsync([mergedSbomSeq]);
      setShowDeleteConfirmModal(false);
      // Reset pagination state
      setCurrentPage(1);
      setTotalPages(0);
      setVerificationFilesDataPagination([]);
    } catch (error) {
      console.error("Error deleting merged SBOM:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleGoBack = () => {
    // Go back to previous state or close modal
    setShowNoVerificationFileErrorModal(false);
  };

  const handleCloseProjectDetailsModal = () => {
    setShowProjectDetailsModal(false);
    setSelectedProjectSeq(null);
  };

  const handleSelectFilesModalClose = () => {
    setShowSelectFilesModal(false);
  };

  const handleSelectFilesModalAdd = (selectedFilesData) => {
    setAddFlag(true);
    setVerificationFilesData(selectedFilesData);
    setShowSelectFilesModal(false);

    // Clear verification files error when files are added
    if (errors.verificationFiles) {
      setErrors(prev => ({
        ...prev,
        verificationFiles: undefined
      }));
    }

    rebuildComponentsFromSelectedFiles(selectedFilesData || []);
  };

  const resolveRowVexContext = useCallback((row) => {
    const scanToolCd = resolveVexScanToolCd(row?.verificationType);
    if (!scanToolCd || row?.verificationSeq == null) return null;
    return { scanToolCd, verificationSeq: row.verificationSeq };
  }, []);

  const handleRemoveFile = (fileToRemove) => {
    const removalIndex = verificationFilesData.findIndex((file) => {
      if (fileToRemove.mergedSbomVerificationSeq != null) {
        return String(file.mergedSbomVerificationSeq) === String(fileToRemove.mergedSbomVerificationSeq);
      }
      if (fileToRemove.verificationSeq == null) return false;
      return (
        String(file.verificationSeq) === String(fileToRemove.verificationSeq) &&
        file.verificationType === fileToRemove.verificationType
      );
    });
    if (removalIndex < 0) return;
    setAddFlag(true);
    const updatedFiles = [...verificationFilesData];
    updatedFiles.splice(removalIndex, 1);

    setVerificationFilesData(updatedFiles);

    // Clear verification files error when files are removed
    if (errors.verificationFiles) {
      setErrors(prev => ({
        ...prev,
        verificationFiles: undefined
      }));
    }

    rebuildComponentsFromSelectedFiles(updatedFiles);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  // Verification Files table configuration
  const verificationFilesColumns = [
    "verificationVersion",
    "realFileName",
    "projectName",
    "verificationType",
    "action",
  ];

  const verificationFilesColumnConfig = {
    verificationVersion: {
      headerRender: () => t("common.version"),
    },
    realFileName: {
      headerRender: () => t("status.verif-file-name"),
    },
    projectName: {
      headerRender: () => t("common.project-name"),
      customRender: (value, row) => (
        <span
          className={`cursor-pointer hover:underline ${colors.textActive}`}
          data-testid={tid(`verification-file-${row.realFileName}-project-name`)}
          onClick={() => handleProjectClick(row.projectSeq)}
        >
          {value}
        </span>
      ),
    },
    verificationType: {
      headerRender: () => t("common.verification-type"),
      customRender: (value) => formatVerificationType(value),
    },
    action: {
      headerRender: () => t("common.action"),
      customRender: (value, row) => (
        <div className="flex items-center space-x-2" data-testid={tid(`verification-file-${row.realFileName}-actions`)}>
          <button
            type="button"
            onClick={() => handleViewFile(row)}
            className={`${colors.iconMuted} ${colors.hoverTextNeutral} p-1`}
            title="View Details"
            data-testid={tid(`verification-file-${row.realFileName}-view-button`)}
          >
            <DocumentInfoIcon size={20} data-testid={tid(`verification-file-${row.realFileName}-view-icon`)} />
          </button>
          {isEditVerificationFiles && (
            <button
              type="button"
              onClick={() => handleRemoveFile(row)}
              className={`text-red-500 ${colors.hoverTextDangerStrong} p-1`}
              title="Remove"
              data-testid={tid(`verification-file-${row.realFileName}-remove-button`)}
            >
              <TrashIcon size={20} data-testid={tid(`verification-file-${row.realFileName}-remove-icon`)} />
            </button>
          )}
        </div>
      )
    }
  };

  const MergedSbomInfoSection = useMemo(() => {
    if (!sbomMergedData) {
      return <></>;
    }
    return (
      <div className={`${colors.bgPrimary} rounded-lg border ${colors.borderPrimary} p-6`} data-testid={tid("merged-info-section")}>
        <div className="flex items-center justify-between mb-4" data-testid={tid("merged-info-header")}>
          <h2 className={`text-lg font-semibold ${colors.textPrimary} uppercase`} data-testid={tid("merged-info-title")}>
            {isEditMode ? t("project.sbom-merged.edit-merged-sbom-information") : t("project.sbom-merged.merged-sbom-information")}
          </h2>
          {isEditMode ? (
            <div className="flex space-x-3" data-testid={tid("merged-info-edit-actions")}>
              <OutlineButton data-testid={tid("cancel-button")} onClick={handleCancelEdit}>
                {t("common.cancel-button")}
              </OutlineButton>
              <PrimaryButton
                data-testid={tid("save-button")}
                onClick={handleSaveEditForInfo}
                disabled={updateSbomMergedOnly.isPending || editFormData?.mergedName?.trim() === ""}
              >
                {t("common.save-button")}
              </PrimaryButton>
            </div>
          ) : (
            <OutlineButton data-testid={tid("edit-button")} onClick={handleEditClick}>
              {t("project.sbom-merged.edit-information")}
            </OutlineButton>
          )}
        </div>
        <div className="grid grid-cols-4 gap-x-6 gap-y-4" data-testid={tid("merged-info-grid")}>
          {/* Row 1 - First 4 fields */}
          <div data-testid={fieldContainerTestId("creator")}>
            <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("creator")}>
              {t("common.creator")}
            </label>
            {isEditMode ? (
              <div className="h-10 flex items-center">
                <CustomInput
                  type="text"
                  value={formatUserNameWithId(sbomMergedData.createrName, sbomMergedData.createrId)}
                  disabled
                  className="w-full"
                  data-testid={fieldInputTestId("creator")}
                />
              </div>
            ) : (
              <div className="min-h-8 flex items-center" data-testid={fieldValueTestId("creator")}>
                <UserNameWithId name={sbomMergedData.createrName} id={sbomMergedData.createrId} layout="inline" />
              </div>
            )}
          </div>
          <div data-testid={fieldContainerTestId("create-at")}>
            <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("create-at")}>
              {t("common.create-at")}
            </label>
            {isEditMode ? (
              <div className="h-10 flex items-center">
                <CustomInput
                  type="text"
                  value={sbomMergedData.createdAt ? formatDateTime(sbomMergedData.createdAt) : ""}
                  disabled
                  className="w-full"
                  data-testid={fieldInputTestId("create-at")}
                />
              </div>
            ) : (
              <p className={`text-sm ${colors.textPrimary} h-8 flex items-center`} data-testid={fieldValueTestId("create-at")}>
                {sbomMergedData.createdAt ? formatDateTime(sbomMergedData.createdAt) : "---"}
              </p>
            )}
          </div>
          <div data-testid={fieldContainerTestId("last-edited")}>
            <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("last-edited")}>
              {t("common.last-modified-by")}
            </label>
            {isEditMode ? (
              <div className="h-10 flex items-center">
                <CustomInput
                  type="text"
                  value={formatUserNameWithId(sbomMergedData.updaterName, sbomMergedData.updaterId)}
                  disabled
                  className="w-full"
                  data-testid={fieldInputTestId("last-edited")}
                />
              </div>
            ) : (
              <div className="min-h-8 flex items-center" data-testid={fieldValueTestId("last-edited")}>
                <UserNameWithId name={sbomMergedData.updaterName} id={sbomMergedData.updaterId} layout="inline" />
              </div>
            )}
          </div>
          <div data-testid={fieldContainerTestId("last-modified")}>
            <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("last-modified")}>
              {t("common.last-modified-date")}
            </label>
            {isEditMode ? (
              <div className="h-10 flex items-center">
                <CustomInput
                  type="text"
                  value={sbomMergedData.updatedAt ? formatDateTime(sbomMergedData.updatedAt) : ""}
                  disabled
                  className="w-full"
                  data-testid={fieldInputTestId("last-modified")}
                />
              </div>
            ) : (
              <p className={`text-sm ${colors.textPrimary} h-8 flex items-center`} data-testid={fieldValueTestId("last-modified")}>
                {sbomMergedData.updatedAt ? formatDateTime(sbomMergedData.updatedAt) : "---"}
              </p>
            )}
          </div>

          {/* Divider */}
          {!isEditMode && (
            <div className={`col-span-4 border-t ${colors.borderSection}`} data-testid={tid("merged-info-divider")}></div>
          )}

          {/* Row 2 - Last 4 fields */}
          <div data-testid={fieldContainerTestId("merged-name")}>
            <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("merged-name")}>
              {t("project.sbom-merged.merged-sbom-name")}
              {isEditMode && <span className={`${colors.textRequired}`}> *</span>}
            </label>
            {isEditMode ? (
              <div className="h-10 flex items-center">
                <CustomInput
                  type="text"
                  value={editFormData.mergedName || ""}
                  onChange={(e) => handleInputChange("mergedName", e.target.value)}
                  className="w-full"
                  maxLength={256}
                  data-testid={fieldInputTestId("merged-name")}
                />
              </div>
            ) : (
              <p className={`text-sm ${colors.textPrimary} overflow-hidden break-words`} data-testid={fieldValueTestId("merged-name")}>
                {sbomMergedData.mergedName || "---"}
              </p>
            )}
          </div>
          <div data-testid={fieldContainerTestId("copyright")}>
            <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("copyright")}>
              {t("project.sbom-merged.copyright")}
            </label>
            {isEditMode ? (
              <>
                <div className="h-10 flex items-center">
                  <CustomInput
                    type="text"
                    value={editFormData.copyright || ""}
                    onChange={(e) => handleInputChange("copyright", e.target.value)}
                    className="w-full"
                    maxLength={CHARACTER_LENGTH.COMMENT}
                    data-testid={fieldInputTestId("copyright")}
                  />
                </div>
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("copyright-char-count")}>
                  {(editFormData.copyright || "").length}/{CHARACTER_LENGTH.COMMENT}
                </p>
              </>
            ) : (
              <p className={`text-sm ${colors.textPrimary} overflow-hidden break-words`} data-testid={fieldValueTestId("copyright")}>
                {sbomMergedData.copyright || "---"}
              </p>
            )}
          </div>
          <div className="col-span-2" data-testid={fieldContainerTestId("remark")}>
            <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("remark")}>
              {t("project.sbom-merged.remark")}
            </label>
            {isEditMode ? (
              <>
                <div className="h-10 flex items-center">
                  <CustomInput
                    type="text"
                    value={editFormData.remark || ""}
                    onChange={(e) => handleInputChange("remark", e.target.value)}
                    className="w-full"
                    maxLength={CHARACTER_LENGTH.REMARK}
                    data-testid={fieldInputTestId("remark")}
                  />
                </div>
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("remark-char-count")}>
                  {(editFormData.remark || "").length}/{CHARACTER_LENGTH.REMARK}
                </p>
              </>
            ) : (
              <p className={`text-sm ${colors.textPrimary} overflow-hidden break-words`} data-testid={fieldValueTestId("remark")}>
                {sbomMergedData.remark || "---"}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }, [t, sbomMergedData, colors, isEditMode, editFormData, updateSbomMergedOnly.isPending]);

  const handleProjectClick = (projectSeq) => {
    setSelectedProjectSeq(projectSeq);
    setShowProjectDetailsModal(true);
  };

  if (getData.isLoading || !sbomMergedData) {
    return (
      <div data-testid={tid("loading")}>
        <LoadingSpinner data-testid={tid("loading-spinner")} />
      </div>
    );
  }

  return (
    <div className={`min-h-full ${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header */}
      <div className="pb-5" data-testid={tid("header")}>
        <div className="space-y-3" data-testid={tid("header-content")}>
          <Breadcrumb data-testid={tid("breadcrumb")} items={breadcrumbItems} />
          <div className="flex items-center space-x-3 justify-between" data-testid={tid("title-row")}>
            <h1 className={`truncate text-2xl font-bold ${colors.textPrimary}`} data-testid={tid("title")}>
              {sbomMergedData?.mergedName || "Loading..."}
            </h1>

            {/* Export SBOM Report button */}
            <PrimaryButton
              data-testid={tid("export-sbom-button")}
              onClick={() => setShowExportModal(true)}
              disabled={isExporting}
            >
              {isExporting ? t("project.verification-report.exporting", "Exporting...") : t("project.verification-report.export-sbom-report")}
            </PrimaryButton>
          </div>
        </div>
      </div>

      <div className="space-y-6" data-testid={tid("body")}>
        {/* Merged SBOM Information Section */}
        {MergedSbomInfoSection}

        {/* Verification Files Section */}
        <div className={`${colors.bgPrimary} rounded-lg border ${colors.borderPrimary} p-6`} data-testid={tid("verification-files-section")}>
          <div className="flex items-center justify-between mb-4" data-testid={tid("verification-files-header")}>
            <h2 className={`text-lg font-semibold ${colors.textPrimary} uppercase`} data-testid={tid("verification-files-title")}>
              {t("project.sbom-merged.verification-project-versions")} ({verificationFilesData?.length || 0})
            </h2>
            {isEditVerificationFiles ? (
              <div className="flex items-center space-x-3" data-testid={tid("verification-files-edit-actions")}>
                <OutlineButton data-testid={tid("verification-files-cancel-button")} onClick={handleCancelVerificationFiles}>
                  {t("common.cancel-button")}
                </OutlineButton>
                <PrimaryButton
                  data-testid={tid("verification-files-save-button")}
                  onClick={handleSaveVerificationFiles}
                  disabled={updateSbomMerged.isPending || isRebuildingComponents}
                >
                  {t("common.save-button")}
                </PrimaryButton>
              </div>
            ) : (
              <OutlineButton data-testid={tid("edit-verification-files-button")} onClick={handleEditVerificationFiles}>
                {t("project.sbom-merged.merge-other")}
              </OutlineButton>
            )}
          </div>
          <div
            className={errors.verificationFiles ? `border text-red-500 rounded-lg p-4` : ""}
            data-testid={tid("verification-files-table-wrapper")}
          >
            <TableWithPagination
              columns={verificationFilesColumns}
              columnConfig={verificationFilesColumnConfig}
              data={verificationFilesDataPagination}
              onRowClick={() => { }}
              loading={getVerificationFiles.isPending}
              totalPages={totalPages}
              totalData={verificationFilesData?.length}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              maxRowsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChangeWithReset}
              hasCheckbox={false}
              hasRadioButton={false}
              rowKey="realFileName"
              shouldShowGoToPage={true}
              showItemsPerPageSelector={true}
              scrollY="600px"
              dataTestId={tid("verification-files-table")}
            />
          </div>
          {errors.verificationFiles && verificationFilesData.length > 0 && (
            <p className={`text-red-500 text-sm mt-2`} data-testid={tid("verification-files-error")}>
              {t(errors.verificationFiles)}
            </p>
          )}
          {isEditVerificationFiles && (
            <div className="flex justify-center mt-6" data-testid={tid("add-verification-file-wrapper")}>
              <button
                type="button"
                onClick={handleAddVerificationFile}
                data-testid={tid("add-verification-file-button")}
                className={`w-full inline-flex items-center justify-center px-6 py-2 border-2 border-dashed ${colors.borderOutLineButton} rounded-lg text-sm font-semibold ${colors.textActive} ${colors.bgAddVerificationFileBtn} transition-all duration-200`}
              >
                <PlusCircleIcon size={20} className="mr-2" data-testid={tid("add-verification-file-icon")} />
                {t("project.verification.add-verification-project-versions")}
              </button>
            </div>
          )}
        </div>

        {/* Component List Section */}
        <div className={`${colors.bgPrimary} rounded-lg border ${colors.borderPrimary} p-6`} data-testid={tid("component-list-section")}>
          <div
            data-testid={tid("component-list-toggle")}
            className="flex items-center justify-between mb-4 cursor-pointer"
            onClick={() => setIsComponentListCollapsed(!isComponentListCollapsed)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsComponentListCollapsed(!isComponentListCollapsed);
              }
            }}
            aria-label={isComponentListCollapsed ? "Expand Component List" : "Collapse Component List"}
          >
            <div className="flex items-center gap-3 flex-wrap" data-testid={tid("component-list-header")}>
              <ChevronUpIcon
                className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${isComponentListCollapsed ? "rotate-180" : ""}`}
                data-testid={tid("component-list-chevron")}
              />
              <h2 className={`text-lg font-semibold ${colors.textPrimary} uppercase`} data-testid={tid("component-list-title")}>
                {t("project.sbom-merged.component-list")} ({sbomMergedComponentList?.length || 0})
              </h2>
            </div>
          </div>
          {!isComponentListCollapsed && (
            <div data-testid={tid("component-list-table-wrapper")}>
              <ComponentTable
                data={sbomMergedComponentList || []}
                licensePolicyData={[]}
                className=""
                maxRowsPerPage={10}
                showPagination={true}
                isEditingAllowed={false}
                seq={isLiveVexQuery ? "componentSeq" : "mergedSbomComponentSeq"}
                isFossIDDependency={false}
                scrollable={true}
                showIcon={false}
                validationMode="export"
                showShieldFilter
                allowVexReview={isLiveVexQuery}
                readOnlyVexReview={isLiveVexQuery}
                resolveRowVexContext={isLiveVexQuery ? resolveRowVexContext : null}
                editableFrozenVexSnapshot={!isLiveVexQuery && canEditMergedVex}
                frozenVexSnapshot={
                  !isLiveVexQuery && mergedSbomSeq
                    ? {
                        source: VEX_SNAPSHOT_SOURCE.MERGED_SBOM,
                        mergedSbomSeq: Number(mergedSbomSeq),
                      }
                    : null
                }
              />
              {errors.componentNames && (
                <p className={`${colors.textDanger} text-sm mt-2`} data-testid={tid("component-name-error")}>
                  {t(errors.componentNames)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Verification File Details Modal */}
      <VerificationFileDetailsModal
        data-testid={tid("file-details-modal")}
        isOpen={showFileDetailsModal}
        onClose={handleCloseFileDetailsModal}
        file={selectedFileForView}
      />

      {/* Select Verification Files Modal */}
      <SelectVerificationFilesForSbomMergeModal
        data-testid={tid("select-files-modal")}
        isOpen={showSelectFilesModal}
        onClose={handleSelectFilesModalClose}
        onAdd={handleSelectFilesModalAdd}
        initialSelectedFiles={verificationFilesData}
      />

      {/* No Verification File Error Modal */}
      <NoVerificationFileErrorModal
        data-testid={tid("no-verification-file-error-modal")}
        isOpen={showNoVerificationFileErrorModal}
        onDelete={handleDeleteMergedSbom}
        onGoBack={handleGoBack}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid={tid("delete-modal")}
        isOpen={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        title={t("project.sbom-merged.confirm-delete-merged-sbom-title")}
        description={t("project.sbom-merged.confirm-delete-merged-sbom-description", { count: 1 })}
        loading={deleteLoading}
      />

      {/* Project Details Modal */}
      <ProjectDetailsModal
        data-testid={tid("project-details-modal")}
        isOpen={showProjectDetailsModal}
        onClose={handleCloseProjectDetailsModal}
        projectSeq={selectedProjectSeq}
      />
      {/* Merge Tooltip Security Information Modal */}
      <CveMergedModal
        data-testid={tid("cve-merged-modal")}
        show={isMergeTooltipOpen}
        onClose={() => {
          setIsMergeTooltipOpen(false);
        }}
        data={cveMergedData}
      />
      <ComponentInformationModal
        data-testid={tid("component-modal")}
        show={isComponentModalOpen}
        onClose={() => {
          setIsComponentModalOpen(false);
          setSelectedComponentData(null);
        }}
        componentData={selectedComponentData}
      />
      <LicenseInformationModal
        data-testid={tid("license-modal")}
        show={isLicenseInformationModalOpen}
        onClose={() => {
          setIsLicenseInformationModalOpen(false);
          setSelectedLicenseInformationData(null);
        }}
        componentLicense={selectedLicenseInformationData}
      />

      {/* Export SBOM Report Modal */}
      <ExportSbomReportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        exporting={isExporting}
        onExport={handleExportSbomReport}
        data-testid={tid("export-modal")}
      />
    </div>
  );
};

export default SbomMergeDetail;
